import type { ShiftRQSchema } from '@libs/data-access/queries'
import {
  contractService,
  labelsService,
  planningEngineService,
  queryClientOptions,
  restService,
  shiftService,
  weeklyScheduleService
} from '@libs/data-access/queries'
import { ConditionalWrap, Modal, Tooltip } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { QueriesRequestsStatus } from '@spa/features/Queries'
import { useMemoSelector } from '@spa/redux/hooks'
import { LockedAlert } from '@spa/scenes/Plannings/Forms/components'
import color from 'color'
import moment from 'moment'
import React from 'react'
import type { FC } from 'react'
import { useDispatch } from 'react-redux'

import { usePadSettings } from '../../hooks'
import { controllersActions } from '../../redux/modules/controllers'
import { getParams } from '../../redux/selectors'
import { BlockHoursInfo } from '../RealHoursForms/ShiftRealHoursForm/components/BlockHoursInfo/BlockHoursInfo'
import { useRestTypeOptions } from '../RealHoursForms/ShiftRealHoursForm/hooks/useShiftRealHoursForm'
import { UnplannedBlockHoursInfo } from '../RealHoursForms/UnplannedShiftRealHoursForm/components/UnplannedBlockHoursInfo/UnplannedBlockHoursInfo'
import { HeaderInfo } from '../RealHoursForms/components'

import styles from './UnvalidateShiftForm.module.css'
import { useUnvalidateShiftForm } from './hooks'

type Props = {
  shift: ShiftRQSchema
  isManager: boolean
  canInvalidate: boolean
  formSuccess: (data) => void
}
export const UnvalidateShiftForm: FC<Props> = ({
  isManager,
  canInvalidate,
  shift,
  formSuccess
}) => {
  const { t } = usePlanningFormTranslation()
  const dispatch = useDispatch()
  const params = useMemoSelector(getParams)
  const { mutateAsync: updateShift } = shiftService.api.useUpdateShift()

  const contractQuery = contractService.contract.useFetchOne(
    {
      pathParams: {
        user_contract_id: Number(shift.user_contract_id)
      }
    },
    {
      enabled: Boolean(shift.user_contract_id),
      staleTime: queryClientOptions.time.STATIC
    }
  )
  const restTypeOptions = useRestTypeOptions()
  const { methods } = useUnvalidateShiftForm({ shift })
  const { handleSubmit, watch, getValues } = methods

  const { did_not_show, witheld_from_pay } = watch()

  const queryShiftWeeklySchedule = weeklyScheduleService.api.useFetchOne(
    {
      pathParams: { id: shift.weeklyschedule_id }
    },
    { staleTime: queryClientOptions.time.STATIC }
  )
  const isPublished = queryShiftWeeklySchedule.data?.published
  const labelId = shift.label_id

  // Load all Labels of all teams of the account
  const labelsQuery = labelsService.api.useFetchAll(
    {},
    { staleTime: queryClientOptions.time.STATIC }
  )

  const shiftLabel = labelsQuery.data?.find(label => label.id === labelId)

  const labelColor = shiftLabel?.color
    ? color(shiftLabel.color).toString()
    : undefined

  const associatedRestQuery = restService.api.useFetchRest(
    {
      pathParams: { id: Number(shift.rest_id) }
    },
    {
      enabled: Boolean(shift.rest_id)
    }
  )

  const rest = associatedRestQuery.data

  // The default rest is: temporary_unavailability
  const shiftRest = rest?.rest_type
    ? restTypeOptions.find(restOption => restOption.value === rest?.rest_type)
    : restTypeOptions[1]

  const { hasSnapPad, hasSnapPadBreaksEnable } = usePadSettings()

  const shiftIsLocked = Boolean(shift.locked_at)
  const shiftValidated = shift?.validated_at !== null
  const validatorById = shift?.validator
  const shiftValidatedDate = moment(shift.validated_at).format(
    'dddd DD MMMM YYYY'
  )

  const onSubmit = () => {
    updateShift({
      pathParams: { shiftId: shift.id },
      payload: getValues()
    }).then(data => {
      // Unvalidate the month view in RQ
      planningEngineService.row.invalidateRows()
      // Refresh redux because... week view and others view are in redux
      if (params.date) {
        dispatch(controllersActions.planningSilentRefresh(params))
      }
      formSuccess(data)
    })
  }

  if (!contractQuery.data) {
    return (
      <QueriesRequestsStatus
        statuses={[contractQuery.status]}
        errors={[contractQuery.error]}
      />
    )
  }

  return (
    <>
      <Modal.Header title={t('unvalidateShiftForm.index.validatedShift')} />
      <form onSubmit={handleSubmit(onSubmit)} className='flex grow flex-col'>
        <Modal.Content>
          {shiftIsLocked && <LockedAlert />}
          <HeaderInfo
            membershipId={contractQuery.data.membership_id}
            date={shift.starts_at}
          />
          {shift?.planification_type === 'unscheduled' ? (
            <UnplannedBlockHoursInfo
              shift={shift}
              color={labelColor}
              isManager={isManager}
              hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
            />
          ) : (
            <BlockHoursInfo
              shift={shift}
              color={labelColor}
              hasSnapPad={hasSnapPad ?? false}
              didNotShow={did_not_show}
              witheldFromPay={witheld_from_pay}
              restType={shiftRest}
              restNote={rest?.note}
              isManager={isManager}
              hasSnapPadBreakEnable={hasSnapPadBreaksEnable}
            />
          )}

          {shiftValidated && (
            <span className={styles.TopInfo}>
              {t('unvalidateShiftForm.index.validatedShiftBy', {
                user: validatorById,
                date: shiftValidatedDate,
                time: shift.validated_at
              })}
            </span>
          )}
        </Modal.Content>
        <Modal.Footer>
          {!shiftIsLocked && isManager && canInvalidate && (
            <ConditionalWrap
              condition={!isPublished}
              wrap={children => (
                <div className={styles.fill}>
                  <Tooltip
                    id='invalidateTooltip'
                    position='top'
                    label={t('unvalidateShiftForm.index.invalidateTooltip')}
                  >
                    <div>{children}</div>
                  </Tooltip>
                </div>
              )}
            >
              <Modal.Footer.Danger
                onClick={onSubmit}
                variant='secondary'
                fillWidth
                disabled={!isPublished}
              >
                {t('unvalidateShiftForm.index.invalidateShift')}
              </Modal.Footer.Danger>
            </ConditionalWrap>
          )}
        </Modal.Footer>
      </form>
    </>
  )
}
